import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Check if user is already logged in on app load
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const authData = localStorage.getItem('shiv_admin_auth');
        if (authData) {
          // Verify token with backend
          try {
            const response = await authAPI.verify();
            if (response.success) {
              setIsAuthenticated(true);
              setUser(response.user);
            } else {
              localStorage.removeItem('shiv_admin_auth');
            }
          } catch (error) {
            console.error('Token verification failed:', error);
            localStorage.removeItem('shiv_admin_auth');
          }
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        localStorage.removeItem('shiv_admin_auth');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (username, password) => {
    try {
      const response = await authAPI.login(username, password);
      
      if (response.success) {
        const authData = {
          access_token: response.token || response.access_token,
          user: response.user,
          expires_in: response.expires_in,
          timestamp: new Date().getTime()
        };

        localStorage.setItem('shiv_admin_auth', JSON.stringify(authData));
        setIsAuthenticated(true);
        setUser(response.user);
        
        return { success: true };
      } else {
        return { 
          success: false, 
          error: response.error || 'Login failed' 
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.message || 'Login failed. Please try again.' 
      };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('shiv_admin_auth');
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 